import { User, Session, PlanTier, PLAN_DETAILS } from '../types';

const BRIDGE_URL = import.meta.env.VITE_BRIDGE_URL || '';
const SESSION_KEY = 'holistic_author_session';
const USER_KEY = 'holistic_author_user';

// Simple hash function for passwords (in production, use bcrypt on server)
const hashPassword = async (password: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(password + 'holistic_salt_2024');
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

// Generate a simple session token
const generateToken = (): string => {
    return crypto.randomUUID() + '-' + Date.now().toString(36);
};

// ============ LOCAL STORAGE HELPERS ============

export const getStoredSession = (): Session | null => {
    try {
        const stored = localStorage.getItem(SESSION_KEY);
        if (!stored) return null;
        const session: Session = JSON.parse(stored);
        if (session.expiresAt < Date.now()) {
            localStorage.removeItem(SESSION_KEY);
            return null;
        }
        return session;
    } catch {
        return null;
    }
};

export const getStoredUser = (): User | null => {
    try {
        const stored = localStorage.getItem(USER_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch {
        return null;
    }
};

const storeSession = (session: Session, user: User): void => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// Update just the user in localStorage (for plan upgrades, etc.)
export const updateStoredUser = (user: User): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
};

const clearSession = (): void => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(USER_KEY);
};

// ============ BACKEND COMMUNICATION ============

interface AuthResponse {
    success: boolean;
    user?: User;
    error?: string;
}

const callAuthEndpoint = async (action: string, payload: object): Promise<AuthResponse> => {
    if (!BRIDGE_URL) {
        // Fallback to local-only mode for development
        console.warn('No BRIDGE_URL configured, using local-only auth');
        return { success: false, error: 'Backend not configured' };
    }

    try {
        // GAS requires special handling for CORS
        // Using text/plain avoids preflight OPTIONS request
        const response = await fetch(BRIDGE_URL, {
            method: 'POST',
            redirect: 'follow', // GAS redirects, we need to follow
            headers: {
                'Content-Type': 'text/plain;charset=utf-8',
            },
            body: JSON.stringify({ action, ...payload })
        });

        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch {
            console.error('Failed to parse response:', text);
            return { success: false, error: 'Respuesta inválida del servidor' };
        }
    } catch (error) {
        console.error('Auth endpoint error:', error);
        return { success: false, error: 'Error de conexión con el servidor' };
    }
};

// ============ EMAIL VERIFICATION ============

export const sendVerificationCode = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
        // Use PHP endpoint on IONOS hosting (avoids GAS MailApp quota limits)
        const response = await fetch('https://yournovel.app/api/verify.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.toLowerCase().trim() })
        });
        const result = await response.json();
        if (result.success) return { success: true };
        return { success: false, error: result.error || 'Error enviando código de verificación' };
    } catch (err) {
        console.error('Verification email error:', err);
        return { success: false, error: 'Error de conexión. Inténtalo de nuevo.' };
    }
};

export const verifyEmailCode = async (email: string, code: string): Promise<{ success: boolean; error?: string }> => {
    const response = await callAuthEndpoint('verifyEmailCode', { email: email.toLowerCase().trim(), code });
    if (response.success && (response as any).verified) return { success: true };
    return { success: false, error: (response as any).error || 'Error verificando código' };
};

// ============ PASSWORD RECOVERY ============

export const sendPasswordResetCode = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
        const response = await fetch('https://yournovel.app/api/verify.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: email.toLowerCase().trim(), purpose: 'reset' })
        });
        const result = await response.json();
        if (result.success) return { success: true };
        return { success: false, error: result.error || 'Error enviando código de recuperación' };
    } catch (err) {
        console.error('Password reset code error:', err);
        return { success: false, error: 'Error de conexión. Inténtalo de nuevo.' };
    }
};

export const resetPassword = async (email: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    const passwordHash = await hashPassword(newPassword);
    const response = await callAuthEndpoint('resetPassword', {
        email: email.toLowerCase().trim(),
        passwordHash
    });
    if (response.success && (response as any).updated) return { success: true };
    return { success: false, error: (response as any).error || 'Error al restablecer la contraseña' };
};

// ============ AUTH FUNCTIONS ============

export const register = async (
    email: string,
    password: string,
    name: string
): Promise<{ success: boolean; user?: User; error?: string }> => {

    // Validate inputs
    if (!email || !password || !name) {
        return { success: false, error: 'Todos los campos son obligatorios' };
    }
    if (password.length < 6) {
        return { success: false, error: 'La contraseña debe tener al menos 6 caracteres' };
    }
    if (!email.includes('@')) {
        return { success: false, error: 'Email inválido' };
    }

    const passwordHash = await hashPassword(password);

    // Try to register with backend
    const backendResponse = await callAuthEndpoint('register', {
        email: email.toLowerCase().trim(),
        passwordHash,
        name: name.trim()
    });

    if (backendResponse.success && backendResponse.user) {
        // Create session
        const session: Session = {
            userId: backendResponse.user.id,
            email: backendResponse.user.email,
            token: generateToken(),
            expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000) // 7 days
        };
        storeSession(session, backendResponse.user);
        return { success: true, user: backendResponse.user };
    }

    // Fallback: Create local-only user for development
    if (!BRIDGE_URL) {
        const localUser: User = {
            id: 'local_' + Date.now(),
            email: email.toLowerCase().trim(),
            name: name.trim(),
            plan: 'free',
            booksRemaining: 0,
            createdAt: Date.now(),
            lastLogin: Date.now(),
            regenerationsUsed: 0,
            auditsUsed: 0,
            usagePeriodStart: Date.now()
        };
        const session: Session = {
            userId: localUser.id,
            email: localUser.email,
            token: generateToken(),
            expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000)
        };
        storeSession(session, localUser);
        return { success: true, user: localUser };
    }

    return { success: false, error: backendResponse.error || 'Error al registrar' };
};

export const login = async (
    email: string,
    password: string
): Promise<{ success: boolean; user?: User; error?: string }> => {

    if (!email || !password) {
        return { success: false, error: 'Email y contraseña son obligatorios' };
    }

    const passwordHash = await hashPassword(password);

    const backendResponse = await callAuthEndpoint('login', {
        email: email.toLowerCase().trim(),
        passwordHash
    });

    if (backendResponse.success && backendResponse.user) {
        const session: Session = {
            userId: backendResponse.user.id,
            email: backendResponse.user.email,
            token: generateToken(),
            expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000)
        };
        storeSession(session, backendResponse.user);
        return { success: true, user: backendResponse.user };
    }

    // Fallback: Check local storage for dev mode
    if (!BRIDGE_URL) {
        const storedUser = getStoredUser();
        if (storedUser && storedUser.email === email.toLowerCase().trim()) {
            const session: Session = {
                userId: storedUser.id,
                email: storedUser.email,
                token: generateToken(),
                expiresAt: Date.now() + (7 * 24 * 60 * 60 * 1000)
            };
            storeSession(session, storedUser);
            return { success: true, user: storedUser };
        }
    }

    return { success: false, error: backendResponse.error || 'Credenciales incorrectas' };
};

export const logout = (): void => {
    clearSession();
};

export const isAuthenticated = (): boolean => {
    const session = getStoredSession();
    return session !== null && session.expiresAt > Date.now();
};

export const updateUserPlan = async (
    userId: string,
    newPlan: PlanTier
): Promise<{ success: boolean; user?: User; error?: string }> => {

    const planDetails = PLAN_DETAILS[newPlan];

    // Update backend
    const backendResponse = await callAuthEndpoint('updatePlan', {
        userId,
        plan: newPlan,
        booksRemaining: planDetails.booksLimit
    });

    if (backendResponse.success && backendResponse.user) {
        // Update local storage
        const session = getStoredSession();
        if (session) {
            storeSession(session, backendResponse.user);
        }
        return { success: true, user: backendResponse.user };
    }

    // Fallback: Update local user
    if (!BRIDGE_URL) {
        const storedUser = getStoredUser();
        if (storedUser) {
            const updatedUser: User = {
                ...storedUser,
                plan: newPlan,
                booksRemaining: planDetails.booksLimit
            };
            const session = getStoredSession();
            if (session) {
                storeSession(session, updatedUser);
            }
            return { success: true, user: updatedUser };
        }
    }


    return { success: false, error: backendResponse.error || 'Error al actualizar plan' };
};

/**
 * Register a new book with the backend, which decrements booksRemaining in the spreadsheet.
 * Returns the updated booksRemaining count from the backend.
 */
export const registerBook = async (
    userId: string,
    projectMeta: { id: string; title: string }
): Promise<{ success: boolean; remaining?: number; error?: string }> => {
    const backendResponse = await callAuthEndpoint('registerBook', {
        userId,
        projectMeta
    });

    if (backendResponse.success) {
        // Update local user with new booksRemaining from backend
        const storedUser = getStoredUser();
        if (storedUser && (backendResponse as any).remaining !== undefined) {
            const updatedUser = { ...storedUser, booksRemaining: (backendResponse as any).remaining };
            const session = getStoredSession();
            if (session) {
                storeSession(session, updatedUser);
            }
        }
        return { success: true, remaining: (backendResponse as any).remaining };
    }

    // Fallback: optimistic local decrement if backend fails
    const currentUser = getStoredUser();
    if (currentUser) {
        const updatedUser = { ...currentUser, booksRemaining: Math.max(0, currentUser.booksRemaining - 1) };
        const session = getStoredSession();
        if (session) {
            storeSession(session, updatedUser);
        }
        return { success: true, remaining: updatedUser.booksRemaining };
    }

    return { success: false, error: backendResponse.error || 'Error registering book' };
};

/**
 * @deprecated Use registerBook instead — this only did a local decrement that got overwritten by refreshUserProfile.
 */
export const refreshUserBooks = (currentUser: User): User => {
    const updatedUser = { ...currentUser, booksRemaining: Math.max(0, currentUser.booksRemaining - 1) };
    const session = getStoredSession();
    if (session) {
        storeSession(session, updatedUser);
    }
    return updatedUser;
};

export const incrementUsage = async (
    userId: string,
    type: 'regeneration' | 'audit'
): Promise<{ success: boolean; user?: User; error?: string }> => {
    const backendResponse = await callAuthEndpoint('incrementUsage', {
        userId,
        type
    });

    if (backendResponse.success && backendResponse.user) {
        const session = getStoredSession();
        if (session) {
            storeSession(session, backendResponse.user);
        }
        return { success: true, user: backendResponse.user };
    }

    if (!BRIDGE_URL) {
        // Dev mode simulation
        const storedUser = getStoredUser();
        if (storedUser) {
            const updatedUser = { ...storedUser };
            if (type === 'regeneration') updatedUser.regenerationsUsed = (updatedUser.regenerationsUsed || 0) + 1;
            if (type === 'audit') updatedUser.auditsUsed = (updatedUser.auditsUsed || 0) + 1;

            const session = getStoredSession();
            if (session) storeSession(session, updatedUser);

            return { success: true, user: updatedUser };
        }
    }

    return { success: false, error: backendResponse.error || 'Error actualizando uso' };
};

/**
 * Fetches the latest user profile from the backend to sync cross-device state.
 * Merges backend data with local user to ensure no fields are lost.
 */
export const refreshUserProfile = async (
    userId: string
): Promise<{ success: boolean; user?: User; error?: string }> => {
    const backendResponse = await callAuthEndpoint('getUserProfile', { userId });

    if (backendResponse.success && backendResponse.user) {
        // Merge with local user to preserve any client-only fields
        const storedUser = getStoredUser();
        const mergedUser = { ...storedUser, ...backendResponse.user };

        const session = getStoredSession();
        if (session) {
            storeSession(session, mergedUser);
        }
        return { success: true, user: mergedUser };
    }

    return { success: false, error: backendResponse.error || 'Error al sincronizar perfil' };
};

export const markFreeGenerationUsed = (currentUser: User): User => {
    const updatedUser = { ...currentUser, freeGenerationUsed: true };
    const session = getStoredSession();
    if (session) {
        storeSession(session, updatedUser);
    }
    // In a real app, we would also sync this to backend
    return updatedUser;
};
